import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck, CheckCircle, X, AlertTriangle, Package,
  Calendar, MapPin, User, FileText, Download,
  Upload, Plus, Search, Filter, RefreshCw, Eye,
  Edit, Trash2, MoreHorizontal, ChevronRight,
  Clock, DollarSign, Scale, Box, Camera, QrCode,
  Barcode, Scan, Printer, Bell, UserCheck, Store,
  Home, Database, PieChart, LineChart, BarChart2,
  CameraOff, CameraOn, QrCodeIcon, BarcodeIcon
} from 'lucide-react';

const ReceivingManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [showCamera, setShowCamera] = useState(false);
  const [scanningMode, setScanningMode] = useState('barcode'); // 'barcode' or 'qr'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Mock data for receiving
  const receivingData = [
    {
      id: 1,
      poNumber: 'PO-2024-001',
      supplier: 'Coffee Co.',
      status: 'pending',
      expectedDate: '2024-01-20',
      receivedDate: null,
      items: [
        { sku: 'INV-001', name: 'Premium Coffee Beans', ordered: 100, received: 0, unit: 'kg', barcode: '1234567890123' },
        { sku: 'INV-005', name: 'Premium Tea Selection', ordered: 50, received: 0, unit: 'boxes', barcode: '1234567890127' }
      ],
      totalValue: 3250.00,
      location: 'Warehouse A',
      notes: 'Priority shipment for weekend sale',
      assignedTo: 'John Smith',
      priority: 'high',
      images: ['coffee-shipment-1.jpg', 'coffee-shipment-2.jpg'],
      documents: ['invoice-001.pdf', 'packing-list-001.pdf']
    },
    {
      id: 2,
      poNumber: 'PO-2024-002',
      supplier: 'Dairy Farm',
      status: 'in-transit',
      expectedDate: '2024-01-18',
      receivedDate: null,
      items: [
        { sku: 'INV-002', name: 'Organic Milk', ordered: 80, received: 0, unit: 'liters', barcode: '1234567890124' }
      ],
      totalValue: 256.00,
      location: 'Cold Storage',
      notes: 'Temperature controlled delivery required',
      assignedTo: 'Sarah Johnson',
      priority: 'medium',
      images: ['milk-shipment-1.jpg'],
      documents: ['invoice-002.pdf']
    },
    {
      id: 3,
      poNumber: 'PO-2024-003',
      supplier: 'Local Bakery',
      status: 'received',
      expectedDate: '2024-01-15',
      receivedDate: '2024-01-15',
      items: [
        { sku: 'INV-003', name: 'Artisan Bread', ordered: 100, received: 100, unit: 'pieces', barcode: '1234567890125' }
      ],
      totalValue: 250.00,
      location: 'Bakery Section',
      notes: 'All items received in good condition',
      assignedTo: 'Mike Wilson',
      priority: 'low',
      images: ['bread-shipment-1.jpg', 'bread-shipment-2.jpg'],
      documents: ['invoice-003.pdf', 'quality-check-003.pdf']
    },
    {
      id: 4,
      poNumber: 'PO-2024-004',
      supplier: 'Fresh Farms',
      status: 'partial',
      expectedDate: '2024-01-17',
      receivedDate: '2024-01-17',
      items: [
        { sku: 'INV-004', name: 'Fresh Vegetables Mix', ordered: 60, received: 45, unit: 'kg', barcode: '1234567890126' }
      ],
      totalValue: 288.00,
      location: 'Produce Section',
      notes: '15kg damaged during transport',
      assignedTo: 'Lisa Brown',
      priority: 'high',
      images: ['vegetables-shipment-1.jpg', 'damage-report-004.jpg'],
      documents: ['invoice-004.pdf', 'damage-report-004.pdf']
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500 text-white';
      case 'in-transit': return 'bg-blue-500 text-white';
      case 'received': return 'bg-green-500 text-white';
      case 'partial': return 'bg-orange-500 text-white';
      case 'cancelled': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'in-transit': return <Truck className="w-4 h-4" />;
      case 'received': return <CheckCircle className="w-4 h-4" />;
      case 'partial': return <AlertTriangle className="w-4 h-4" />;
      case 'cancelled': return <X className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredData = receivingData.filter(item => {
    const matchesSearch = item.poNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.supplier.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSupplier = selectedSupplier === 'all' || item.supplier === selectedSupplier;
    const matchesStatus = activeTab === 'all' || item.status === activeTab;
    return matchesSearch && matchesSupplier && matchesStatus;
  });

  const tabs = [
    { id: 'all', label: 'All', count: receivingData.length },
    { id: 'pending', label: 'Pending', count: receivingData.filter(item => item.status === 'pending').length },
    { id: 'in-transit', label: 'In Transit', count: receivingData.filter(item => item.status === 'in-transit').length },
    { id: 'received', label: 'Received', count: receivingData.filter(item => item.status === 'received').length },
    { id: 'partial', label: 'Partial', count: receivingData.filter(item => item.status === 'partial').length },
    { id: 'cancelled', label: 'Cancelled', count: receivingData.filter(item => item.status === 'cancelled').length }
  ];

  const suppliers = [...new Set(receivingData.map(item => item.supplier))];

  const ReceivingListView = ({ item }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{item.poNumber}</h3>
              <p className="text-sm text-gray-600">{item.supplier}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(item.status)}`}>
              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getPriorityColor(item.priority)}`}>
              {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 text-sm mb-4">
          <div>
            <span className="text-gray-500">Expected Date:</span>
            <p className="font-medium text-gray-900">{item.expectedDate}</p>
          </div>
          <div>
            <span className="text-gray-500">Location:</span>
            <p className="font-medium text-gray-900">{item.location}</p>
          </div>
          <div>
            <span className="text-gray-500">Assigned To:</span>
            <p className="font-medium text-gray-900">{item.assignedTo}</p>
          </div>
          <div>
            <span className="text-gray-500">Total Value:</span>
            <p className="font-medium text-gray-900">${item.totalValue.toLocaleString()}</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">{item.items.length} items</span>
            <span className="text-sm text-gray-600">•</span>
            <span className="text-sm text-gray-600">{item.items.reduce((acc, item) => acc + item.received, 0)}/{item.items.reduce((acc, item) => acc + item.ordered, 0)} received</span>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => alert(`Processing receiving order ${item.poNumber}`)}
              className="px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:scale-105"
            >
              Process
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const ReceivingCard = ({ item }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{item.poNumber}</h3>
            <p className="text-sm text-gray-600">{item.supplier}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(item.status)}`}>
              {getStatusIcon(item.status)}
              <span>{item.status.replace('-', ' ').toUpperCase()}</span>
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
              {item.priority.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Expected Date</p>
            <p className="text-sm font-medium text-gray-900">{new Date(item.expectedDate).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
            <p className="text-sm font-medium text-gray-900">{item.location}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Assigned To</p>
            <p className="text-sm font-medium text-gray-900">{item.assignedTo}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Value</p>
            <p className="text-sm font-medium text-gray-900">${item.totalValue.toLocaleString()}</p>
          </div>
        </div>

        {/* Items List */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Items</h4>
          <div className="space-y-2">
            {item.items.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{product.name}</p>
                  <p className="text-xs text-gray-600">SKU: {product.sku} • Barcode: {product.barcode}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">{product.received}/{product.ordered} {product.unit}</p>
                  <div className="w-20 bg-gray-200 rounded-full h-1 mt-1">
                    <div
                      className="bg-green-500 h-1 rounded-full transition-all duration-300"
                      style={{ width: `${(product.received / product.ordered) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {item.notes && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">{item.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => {
                alert(`Camera scanning activated for ${item.poNumber}`);
                // Mock camera functionality
              }}
              className="p-2 rounded-lg hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors group"
            >
              <Camera className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
            <button 
              onClick={() => {
                alert(`QR Code scanning activated for ${item.poNumber}`);
                // Mock QR scanning functionality
              }}
              className="p-2 rounded-lg hover:bg-green-50 text-gray-600 hover:text-green-600 transition-colors group"
            >
              <QrCode className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
            <button 
              onClick={() => {
                alert(`Barcode scanning activated for ${item.poNumber}`);
                // Mock barcode scanning functionality
              }}
              className="p-2 rounded-lg hover:bg-purple-50 text-gray-600 hover:text-purple-600 transition-colors group"
            >
              <Barcode className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
            <button 
              onClick={() => {
                alert(`Printing documents for ${item.poNumber}`);
                // Mock printing functionality
              }}
              className="p-2 rounded-lg hover:bg-orange-50 text-gray-600 hover:text-orange-600 transition-colors group"
            >
              <Printer className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => {
                alert(`Processing receiving order ${item.poNumber}`);
                // Mock processing functionality
              }}
              className="px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:scale-105"
            >
              Process
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      {/* Header with Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Receiving Management</h2>
            <p className="text-gray-600">Process incoming shipments and verify items</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCamera(!showCamera)}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
            >
              {showCamera ? <CameraOff className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
              {showCamera ? 'Stop Camera' : 'Start Camera'}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Receiving
            </motion.button>
          </div>
        </div>

        {/* Camera Scanner */}
        {showCamera && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Scanner</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setScanningMode('barcode')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    scanningMode === 'barcode'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <BarcodeIcon className="w-4 h-4 inline mr-1" />
                  Barcode
                </button>
                <button
                  onClick={() => setScanningMode('qr')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    scanningMode === 'qr'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <QrCodeIcon className="w-4 h-4 inline mr-1" />
                  QR Code
                </button>
              </div>
            </div>
            <div className="bg-black rounded-lg h-64 flex items-center justify-center">
              <div className="text-center text-white">
                <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm opacity-75">
                  {scanningMode === 'barcode' ? 'Barcode Scanner Active' : 'QR Code Scanner Active'}
                </p>
                <p className="text-xs opacity-50 mt-1">Point camera at {scanningMode === 'barcode' ? 'barcode' : 'QR code'}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Apple-like Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Enhanced Search and Filters */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by PO number or supplier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors shadow-sm"
              />
            </div>
            <div className="relative">
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors shadow-sm appearance-none"
              >
                <option value="all">All Suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier} value={supplier}>{supplier}</option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
            </div>
            <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105">
              <Filter className="w-4 h-4" />
              Advanced Filters
            </button>
          </div>
        </div>
      </div>

      {/* Receiving Items Grid */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-4'}>
        <AnimatePresence mode="wait">
          {filteredData.map((item) => (
            viewMode === 'grid' ? (
              <ReceivingCard key={item.id} item={item} />
            ) : (
              <ReceivingListView key={item.id} item={item} />
            )
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No receiving items found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            Create New Receiving
          </button>
        </div>
      )}
    </div>
  );
};

export default ReceivingManagement; 